import { useCallback, useRef } from 'react';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
} from 'react';

export interface SplitterProps {
  readonly axis: 'x' | 'y';
  /** Current size in px, for `aria-valuenow`. */
  readonly value: number;
  readonly min: number;
  readonly max: number;
  /** Called with the absolute size while dragging and with `step` on arrows. */
  readonly onChange: (px: number) => void;
  /** Invert the drag: the right column grows when the pointer moves left. */
  readonly invert?: boolean;
  readonly label: string;
}

/**
 * A resizable divider that works with a pointer **and** the keyboard.
 *
 * Drag uses pointer capture, so a fast drag that leaves the 7 px strip keeps
 * resizing instead of sticking. Arrow keys move the same clamped value in 16 px
 * steps, which means the divider is reachable and usable without a mouse — the
 * brief asks for keyboard-first operation, and a resize handle that only responds
 * to a drag quietly excludes people.
 */
export function Splitter({ axis, value, min, max, onChange, invert = false, label }: SplitterProps): ReactElement {
  const startRef = useRef<{ pointer: number; size: number } | null>(null);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      startRef.current = { pointer: axis === 'x' ? event.clientX : event.clientY, size: value };
    },
    [axis, value],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const start = startRef.current;
      if (start === null) return;
      const current = axis === 'x' ? event.clientX : event.clientY;
      const delta = current - start.pointer;
      const next = start.size + (invert ? -delta : delta);
      onChange(Math.min(max, Math.max(min, Math.round(next))));
    },
    [axis, invert, max, min, onChange],
  );

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    startRef.current = null;
  }, []);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? 48 : 16;
      const towardsMin = axis === 'x' ? 'ArrowLeft' : 'ArrowUp';
      const towardsMax = axis === 'x' ? 'ArrowRight' : 'ArrowDown';
      if (event.key === towardsMin) {
        event.preventDefault();
        onChange(Math.max(min, value + (invert ? step : -step)));
      } else if (event.key === towardsMax) {
        event.preventDefault();
        onChange(Math.min(max, value + (invert ? -step : step)));
      }
    },
    [axis, invert, max, min, onChange, value],
  );

  return (
    <div
      className="splitter"
      data-axis={axis}
      role="separator"
      tabIndex={0}
      aria-orientation={axis === 'x' ? 'vertical' : 'horizontal'}
      aria-label={label}
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={max}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      title={`${label} — drag or use the arrow keys`}
    />
  );
}
